import type { IDiseaseHistoryData, IGraphData } from 'Components/sq-graphics';
import type { TAlzheimerDiagnosis } from 'Routes/Diagnosis/utils/definitions';
import { alzheimerDiagnoses } from 'Routes/Diagnosis/utils/definitions';
import { getAtcCodesByPlatform } from 'Routes/Medication/Document/config';
import { fbimod } from 'neuro-schemas';

const { calculateScore } = fbimod;

const alzheimGraphOrder: Array<string> = [
  'cdr',
  'adl_iadl',
  'fbiMod',
  'ledd',
  'updrsIII',
  'ftmtrs',
  'dbsEffect',
  'mmse',
  'moca',
  'cerad',
  'tmt',
  'sdmt',
  'pnfl',
  'pTau217',
];

const parkinsonData = (
  docs: Array<IDocumentV2>,
  fm: (id: string) => string,
  uiLang: Locale,
  medicationSettings: IOrganizationNeuroSettings['medicationSettings'] | undefined,
): IDiseaseHistoryData => {
  const clinicalMeters = fm('graph.clinicalMeters');
  const patientSelfReport = fm('graph.patientSelfReport');

  const filledFbiModDocsFound = docs.some(d => d._type === 'fbimod' && typeof calculateScore(d) === 'number');
  const hasAlz = docs.some(
    d => d._type === 'diagnosis' && 'diagnosis' in d && alzheimerDiagnoses.includes(d.diagnosis as TAlzheimerDiagnosis),
  );

  const atcCodes = getAtcCodesByPlatform(medicationSettings, 'parkinson');
  const atcOpts = medicationSettings?.parkinson.localizations?.[uiLang as 'fi' | 'en'];

  const baseData: IDiseaseHistoryData = {
    graphData: {
      common: {
        ledd: {
          data: [],
          title: 'LEDD',
          scale: {
            type: 'linear',
            id: 'ledd',
          },
        },
        updrsIII: {
          data: [],
          title: 'UPDRS III',
          scale: {
            type: 'linear',
            id: 'updrs',
            linearScale: [0, 108],
          },
          additionalScale: { type: 'linear', id: 'mdsupdrs', linearScale: [0, 132] },
        },
        ftmtrs: {
          data: [],
          title: 'FTM-TRS',
          scale: {
            type: 'linear',
            id: 'ftmtrs',
            linearScale: [0, 40],
          },
        },
        dbsEffect: {
          data: [],
          title: fm('updrs.dbsEffect'),
          scale: {
            type: 'linear',
            id: 'updrs',
            linearScale: [0, 108],
          },
          additionalScale: { type: 'linear', id: 'mdsupdrs', linearScale: [0, 132] },
        },
        cdr: {
          data: [],
          title: fm('cognition.cdr.cdr'),
          scale: {
            type: 'custom',
            id: 'cdr',
            customScale: ['0', '0.5', '1', '2', '3'],
          },
          additionalScale: { type: 'linear', id: 'cdrTotalScore', linearScale: [0, 24] },
        },
        adl_iadl: {
          data: [],
          title: 'ADL & IADL',
          scale: {
            type: 'hybridCL',
            id: 'adl',
            linearScale: [0, 6],
            customScale: ['N/A'],
          },
          additionalScale: { type: 'linear', id: 'iadl', linearScale: [0, 8] },
        },
        fbiMod: {
          data: [],
          title: 'FBI-Mod',
          scale: {
            type: 'linear',
            id: 'fbiMod',
            linearScale: [0, 66],
          },
        },
        mmse: {
          data: [],
          title: 'MMSE',
          scale: {
            type: 'hybridCL',
            id: 'mmse',
            linearScale: [0, 30],
            customScale: ['❌'],
          },
        },
        moca: {
          data: [],
          title: 'MOCA',
          scale: {
            type: 'hybridCL',
            id: 'moca',
            linearScale: [0, 30],
            customScale: ['❌'],
          },
        },
        cerad: {
          data: [],
          title: fm('cognition.cerad.cerad'),
          scale: {
            type: 'linear',
            id: 'cerad',
            linearScale: [0, 111],
          },
        },
        tmt: {
          data: [],
          title: 'TMT',
          scale: {
            type: 'hybridLC',
            id: 'tmt',
            linearScale: [0, 240],
            customScale: ['❌'],
          },
        },
        sdmt: {
          data: [],
          title: 'SDMT',
          scale: {
            type: 'linear',
            id: 'sdmt',
            linearScale: [0, 110],
          },
        },
        pnfl: {
          data: [],
          title: 'P-Nfl',
          scale: {
            type: 'linear',
            id: 'P-Nfl',
          },
        },
        pTau217: {
          data: [],
          title: 'P-Tau-217',
          scale: {
            type: 'linear',
            id: 'P-Tau-217',
          },
        },
      },
      selfReporting: {
        eq5d: {
          data: [],
          title: 'EQ-5D-5L',
          scale: {
            type: 'hybridCL',
            linearScale: [0, 1],
            customScale: ['< 0'],
          },
        },
        pdq8: {
          data: [],
          title: 'PDQ-8',
          scale: {
            type: 'linear',
            linearScale: [0, 100],
          },
        },
        bdi: {
          data: [],
          title: 'BDI',
          scale: {
            id: 'bdi',
            type: 'linear',
            linearScale: [0, 63],
          },
        },
      },
    },
    graphDataGroups: {
      common: {
        groupHeader: clinicalMeters,
        open: true,
      },
      selfReporting: {
        open: false,
        groupHeader: patientSelfReport,
        theme: 'myms',
      },
    },
    graphMenuSettings: {
      hideEmpty: true,
    },
    addonData: {
      hoehnYahr: {
        id: 'hoehnYahr',
        type: 'single',
        name: 'Hoehn & Yahr',
        addons: [],
      },
      dbs: {
        id: 'dbs',
        type: 'single',
        name: fm('dbs.title'),
        addons: [],
      },
      thalamotomy: {
        id: 'thalamotomy',
        type: 'single',
        name: fm('thalamotomyHifu.thalamotomy.title'),
        addons: [],
      },
      hifu: {
        id: 'hifu',
        type: 'single',
        name: fm('thalamotomyHifu.hifu.title'),
        addons: [],
      },
      medications: {
        id: 'medications',
        type: 'expansionPanel',
        name: fm('medication.title'),
        expansionPanelTitleDescription: [
          {
            title: fm('graph.atc-codes'),
            style: { fontWeight: 600 },
          },
        ].concat(
          Object.keys(atcOpts ?? {})
            .filter(code => atcCodes.includes(code))
            .map(code => {
              return { title: `${code} - ${atcOpts?.[code]}` };
            }) as [],
        ),
        addons: [],
        expansionPanelShowAlways: true,
      },
      endedMedications: {
        id: 'endedMedications',
        type: 'expansionPanel',
        name: fm('medication.endedMedications'),
        addons: [],
      },
      studies: {
        id: 'studies',
        type: 'expansionPanel',
        name: fm('graph.studies'),
        addons: [],
      },
      diagnosis: {
        id: 'diagnosis',
        type: 'single',
        name: fm('diagnosis.title'),
        addons: [],
      },
      appointments: {
        id: 'appointments',
        type: 'single',
        name: fm('appointments.appointments'),
        addons: [],
      },
      patientSelfReport: {
        id: 'patientSelfReport',
        type: 'single',
        name: fm('graph.patientSelfReport'),
        addons: [],
      },
      topdata: {
        id: 'topdata',
        type: 'single',
        name: 'topdata',
        addons: [],
      },
    },
  };
  const returnableData = structuredClone(baseData);
  const alzheimBaseData = alzheimGraphOrder.reduce(
    (prev, curr) => {
      prev[curr] = baseData.graphData.common[curr] as IGraphData;
      return prev;
    },
    {} as Record<string, IGraphData>,
  );
  if (hasAlz) {
    returnableData.graphData.common = alzheimBaseData;
  }

  const commonGraphKeys = Object.keys(returnableData.graphData.common);
  const commonTitles = commonGraphKeys
    .map(key => returnableData.graphData.common[key]?.title)
    .filter((t): t is string => !!t);
  const selfRepGraphKeys = Object.keys(returnableData.graphData.selfReporting ?? {});
  const selfRepTitles = selfRepGraphKeys
    .map(key => returnableData.graphData.selfReporting?.[key]?.title)
    .filter((t): t is string => !!t);
  if (returnableData.graphDataGroups?.common) {
    returnableData.graphDataGroups.common.emptyInfo = commonTitles;
  }
  if (returnableData.graphDataGroups?.selfReporting) {
    returnableData.graphDataGroups.selfReporting.emptyInfo = selfRepTitles;
  }

  if (!filledFbiModDocsFound) {
    delete returnableData.graphData.common.fbiMod;
  }
  return returnableData;
};

export default parkinsonData;
